import * as THREE from 'three';
import { toonMat, createToonGradientMap } from './materials.js';

// ─── Types ────────────────────────────────────────────────────────────────────

export type MythicKind = 'drum_gun' | 'infinity_blade' | 'boom_bow' | 'grappler' | 'jetpack';

export type MythicUpdateResult = {
  damage: number;
  damageRadius: number;
  recoilImpulse: THREE.Vector3;
  jetpackLift: number;
  grappleTarget: THREE.Vector3 | null;
  hudText: string;
};

// ─── Internal state types ─────────────────────────────────────────────────────

type PickupItem = {
  kind: MythicKind;
  mesh: THREE.Mesh;
  light: THREE.PointLight;
  rotT: number;
};

type BoomProjectile = {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
  pos: THREE.Vector3;
  stage: number; // 1-3
  alive: boolean;
};

type BlastFx = {
  mesh: THREE.Mesh;
  t: number;
};

type SweepMesh = {
  mesh: THREE.Mesh;
  t: number;
};

type GrappleLine = {
  mesh: THREE.Mesh;
  target: THREE.Vector3;
  travelT: number;
  totalT: number;
  startPos: THREE.Vector3;
};

// ─── Spawn positions ──────────────────────────────────────────────────────────

const SPAWN_POSITIONS: Record<MythicKind, THREE.Vector3> = {
  drum_gun:      new THREE.Vector3(5,   1, -35),
  infinity_blade: new THREE.Vector3(-18, 1, -10),
  boom_bow:      new THREE.Vector3(30,  1, -20),
  grappler:      new THREE.Vector3(-52, 1, -52),
  jetpack:       new THREE.Vector3(38,  1,  12),
};

const PICKUP_RADIUS = 2;
const GOLD = 0xffd700;

// ─── Drum Gun state ───────────────────────────────────────────────────────────
const DRUM_ROF = 0.085; // seconds between shots
const DRUM_MAG = 50;
const DRUM_BASE_SPREAD = 0.06;
const DRUM_TIGHT_SPREAD = 0.018;
const DRUM_DAMAGE = 22;
const DRUM_HEAT_PER_SHOT = 0.034;
const DRUM_HEAT_DECAY = 0.42;
const DRUM_OVERHEAT_COOLDOWN = 2.8;

// ─── Boom Bow constants ───────────────────────────────────────────────────────
const BOOM_PROJECTILE_SPEED = 28;
const BOOM_EXPLOSION_RADIUS = 3;
const BOOM_DAMAGE_BASE = 60;
const BOOM_CHARGE_PER_STAGE = 1; // seconds per stage
const BOOM_BLAST_DURATION = 0.3;

// ─── Jetpack constants ────────────────────────────────────────────────────────
const JETPACK_LIFT_BASE = 18;
const JETPACK_FUEL_MAX = 1;
const JETPACK_DEPLETE = 0.4;
const JETPACK_REGEN = 0.15;

// ─── Grappler constants ───────────────────────────────────────────────────────
const GRAPPLE_RANGE = 40;
const GRAPPLE_CHARGES_MAX = 10;
const GRAPPLE_TRAVEL_TIME = 0.6;

// ─── Main class ───────────────────────────────────────────────────────────────

export class FpsMythicWeapons {
  private readonly scene: THREE.Scene;
  private readonly gradient: THREE.DataTexture;
  private readonly pickups: PickupItem[] = [];
  private activeKind: MythicKind | null = null;

  // HUD element
  private readonly hudEl: HTMLDivElement;

  // Drum Gun
  private drumCooldown = 0;
  private drumShotCount = 0; // consecutive shots this burst
  private drumAmmo = DRUM_MAG;
  private drumHeat = 0;
  private drumOverheatCooldown = 0;

  // Infinity Blade
  private bladeSwingCooldown = 0;
  private bladeDashCooldown = 0;
  private sweepMeshes: SweepMesh[] = [];

  // Boom Bow
  private boomChargeT = 0;
  private boomCharging = false;
  private boomProjectiles: BoomProjectile[] = [];
  private boomBlasts: BlastFx[] = [];

  // Grappler
  private grapplerCharges = GRAPPLE_CHARGES_MAX;
  private grapplerCooldown = 0;
  private activeLine: GrappleLine | null = null;
  private grappleLerpResult: THREE.Vector3 | null = null;

  // Jetpack
  private jetpackFuel = JETPACK_FUEL_MAX;

  // previous fire/altFire key states for edge detection
  private prevFire = false;
  private prevAltFire = false;

  constructor(scene: THREE.Scene, mount: HTMLElement) {
    this.scene = scene;
    this.gradient = createToonGradientMap();

    // Spawn all pickups
    for (const [kind, pos] of Object.entries(SPAWN_POSITIONS) as [MythicKind, THREE.Vector3][]) {
      this.spawnPickup(kind, pos);
    }

    // HUD element
    this.hudEl = document.createElement('div');
    this.hudEl.id = 'fps-mythic-hud';
    Object.assign(this.hudEl.style, {
      position: 'fixed',
      bottom: '72px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'rgba(10,8,4,0.82)',
      border: '1.5px solid #ffd700',
      borderRadius: '6px',
      padding: '5px 16px',
      fontFamily: 'sans-serif',
      fontSize: '13px',
      fontWeight: 'bold',
      color: '#ffd700',
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      pointerEvents: 'none',
      zIndex: '9999',
      display: 'none',
    });
    mount.appendChild(this.hudEl);
  }

  // ─── Pickup helpers ─────────────────────────────────────────────────────────

  private spawnPickup(kind: MythicKind, pos: THREE.Vector3): void {
    const geo = new THREE.BoxGeometry(0.55, 0.55, 0.55);
    const mat = toonMat(GOLD, this.gradient, { emissive: GOLD, emissiveIntensity: 0.6 });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.copy(pos);
    mesh.castShadow = true;
    this.scene.add(mesh);

    const light = new THREE.PointLight(GOLD, 1.5, 6);
    light.position.copy(pos);
    this.scene.add(light);

    this.pickups.push({ kind, mesh, light, rotT: Math.random() * Math.PI * 2 });
  }

  private removePickup(item: PickupItem): void {
    item.mesh.removeFromParent();
    (item.mesh.geometry as THREE.BufferGeometry).dispose();
    (item.mesh.material as THREE.Material).dispose();
    item.light.removeFromParent();
    const idx = this.pickups.indexOf(item);
    if (idx >= 0) this.pickups.splice(idx, 1);
  }

  // ─── Public API ─────────────────────────────────────────────────────────────

  tryPickup(playerPos: THREE.Vector3): MythicKind | null {
    for (const item of this.pickups) {
      if (item.mesh.position.distanceTo(playerPos) <= PICKUP_RADIUS) {
        const kind = item.kind;
        this.removePickup(item);
        return kind;
      }
    }
    return null;
  }

  getActiveKind(): MythicKind | null {
    return this.activeKind;
  }

  getHeatRatio(): number {
    if (this.activeKind !== 'drum_gun') return 0;
    return Math.max(0, Math.min(1, this.drumHeat));
  }

  isOverheated(): boolean {
    return this.activeKind === 'drum_gun' && this.drumOverheatCooldown > 0;
  }

  setActive(kind: MythicKind | null): void {
    this.activeKind = kind;
    // Reset per-weapon state on equip
    if (kind === 'drum_gun') {
      this.drumAmmo = DRUM_MAG;
      this.drumCooldown = 0;
      this.drumShotCount = 0;
      this.drumHeat = 0;
      this.drumOverheatCooldown = 0;
    } else if (kind === 'infinity_blade') {
      this.bladeSwingCooldown = 0;
      this.bladeDashCooldown = 0;
    } else if (kind === 'boom_bow') {
      this.boomChargeT = 0;
      this.boomCharging = false;
    } else if (kind === 'grappler') {
      this.grapplerCharges = GRAPPLE_CHARGES_MAX;
      this.grapplerCooldown = 0;
    } else if (kind === 'jetpack') {
      this.jetpackFuel = JETPACK_FUEL_MAX;
    }
    this.updateHud();
  }

  // ─── Update ─────────────────────────────────────────────────────────────────

  update(
    dt: number,
    playerPos: THREE.Vector3,
    playerVel: THREE.Vector3,
    playerYaw: number,
    keys: { fire: boolean; altFire: boolean; space: boolean },
  ): MythicUpdateResult {
    const result: MythicUpdateResult = {
      damage: 0,
      damageRadius: 0,
      recoilImpulse: new THREE.Vector3(),
      jetpackLift: 0,
      grappleTarget: null,
      hudText: '',
    };

    // Animate pickups
    for (const item of this.pickups) {
      item.rotT += dt;
      item.mesh.rotation.y = item.rotT * 1.1;
      item.mesh.rotation.x = Math.sin(item.rotT * 0.7) * 0.18;
      item.mesh.position.y = SPAWN_POSITIONS[item.kind].y + Math.sin(item.rotT * 1.4) * 0.18;
      item.light.position.copy(item.mesh.position);
      // pulse light
      item.light.intensity = 1.2 + Math.sin(item.rotT * 2.5) * 0.4;
    }

    // Animate sweep arcs (blade)
    for (let i = this.sweepMeshes.length - 1; i >= 0; i--) {
      const s = this.sweepMeshes[i]!;
      s.t += dt;
      const u = Math.min(1, s.t / 0.22);
      (s.mesh.material as THREE.MeshBasicMaterial).opacity = 0.7 * (1 - u);
      if (s.t >= 0.22) {
        s.mesh.removeFromParent();
        (s.mesh.geometry as THREE.BufferGeometry).dispose();
        (s.mesh.material as THREE.Material).dispose();
        this.sweepMeshes.splice(i, 1);
      }
    }

    // Animate boom bow projectiles
    this.updateBoomProjectiles(dt, result);

    // Animate blast fx
    for (let i = this.boomBlasts.length - 1; i >= 0; i--) {
      const b = this.boomBlasts[i]!;
      b.t += dt;
      const u = Math.min(1, b.t / BOOM_BLAST_DURATION);
      b.mesh.scale.setScalar(u * BOOM_EXPLOSION_RADIUS * 2);
      (b.mesh.material as THREE.MeshBasicMaterial).opacity = 0.75 * (1 - u);
      if (b.t >= BOOM_BLAST_DURATION) {
        b.mesh.removeFromParent();
        (b.mesh.geometry as THREE.BufferGeometry).dispose();
        (b.mesh.material as THREE.Material).dispose();
        this.boomBlasts.splice(i, 1);
      }
    }

    // Animate grapple line
    if (this.activeLine) {
      this.activeLine.travelT += dt;
      const frac = Math.min(1, this.activeLine.travelT / this.activeLine.totalT);
      const newPos = this.activeLine.startPos.clone().lerp(this.activeLine.target, frac);
      result.grappleTarget = newPos.clone();

      // Update line mesh: stretch from startPos to current lerped pos
      this.updateGrappleLine(this.activeLine.startPos, newPos);

      if (frac >= 1) {
        this.activeLine.mesh.removeFromParent();
        (this.activeLine.mesh.geometry as THREE.BufferGeometry).dispose();
        (this.activeLine.mesh.material as THREE.Material).dispose();
        this.activeLine = null;
        this.grappleLerpResult = null;
      }
    }

    // Weapon-specific logic
    if (!this.activeKind) return result;

    switch (this.activeKind) {
      case 'drum_gun':
        this.updateDrumGun(dt, keys, result);
        break;
      case 'infinity_blade':
        this.updateInfinityBlade(dt, playerPos, playerYaw, keys, result);
        break;
      case 'boom_bow':
        this.updateBoomBow(dt, playerPos, playerYaw, keys, result);
        break;
      case 'grappler':
        this.updateGrappler(dt, playerPos, playerYaw, keys, result);
        break;
      case 'jetpack':
        this.updateJetpack(dt, keys, result);
        break;
    }

    this.prevFire = keys.fire;
    this.prevAltFire = keys.altFire;

    this.updateHud();
    return result;
  }

  // ─── Drum Gun ───────────────────────────────────────────────────────────────

  private updateDrumGun(
    dt: number,
    keys: { fire: boolean },
    result: MythicUpdateResult,
  ): void {
    this.drumCooldown = Math.max(0, this.drumCooldown - dt);
    if (this.drumOverheatCooldown > 0) {
      this.drumOverheatCooldown = Math.max(0, this.drumOverheatCooldown - dt);
      if (this.drumOverheatCooldown <= 0) this.drumHeat = 0;
    } else if (this.drumHeat > 0 && !keys.fire) {
      this.drumHeat = Math.max(0, this.drumHeat - DRUM_HEAT_DECAY * dt);
    }

    if (keys.fire && this.drumCooldown <= 0 && this.drumAmmo > 0 && this.drumOverheatCooldown <= 0) {
      this.drumAmmo--;
      this.drumCooldown = DRUM_ROF;
      this.drumShotCount++;
      this.drumHeat = Math.min(1, this.drumHeat + DRUM_HEAT_PER_SHOT);
      if (this.drumHeat >= 1) this.drumOverheatCooldown = DRUM_OVERHEAT_COOLDOWN;

      const spread = this.drumShotCount >= 3 ? DRUM_TIGHT_SPREAD : DRUM_BASE_SPREAD;
      // recoil
      result.damage = DRUM_DAMAGE;
      result.damageRadius = 0;
      result.recoilImpulse.set(
        (Math.random() - 0.5) * spread * 2,
        0.012,
        0,
      );
      const overheatTag = this.drumOverheatCooldown > 0 ? ' COOLING' : this.drumHeat > 0.75 ? ' HOT' : '';
      result.hudText = `DRUM GUN  ${this.drumAmmo}/${DRUM_MAG}  ${this.drumShotCount >= 3 ? '(tight)' : ''}${overheatTag}`;
    } else if (!keys.fire) {
      this.drumShotCount = 0;
      const overheatTag = this.drumOverheatCooldown > 0 ? ' COOLING' : '';
      result.hudText = `DRUM GUN  ${this.drumAmmo}/${DRUM_MAG}${overheatTag}`;
    }
  }

  // ─── Infinity Blade ─────────────────────────────────────────────────────────

  private updateInfinityBlade(
    dt: number,
    playerPos: THREE.Vector3,
    playerYaw: number,
    keys: { fire: boolean; altFire: boolean },
    result: MythicUpdateResult,
  ): void {
    this.bladeSwingCooldown = Math.max(0, this.bladeSwingCooldown - dt);
    this.bladeDashCooldown = Math.max(0, this.bladeDashCooldown - dt);

    const firePressed = keys.fire && !this.prevFire;
    const altPressed = keys.altFire && !this.prevAltFire;

    if (firePressed && this.bladeSwingCooldown <= 0) {
      this.bladeSwingCooldown = 0.6;
      // Spawn 5 fan-arc meshes
      this.spawnBladeArc(playerPos, playerYaw);
      result.damage = 999; // one-shot bots
      result.damageRadius = 8;
      result.hudText = 'INFINITY BLADE  SWING';
    } else {
      result.hudText = 'INFINITY BLADE';
    }

    // Alt-fire: dash forward
    if (altPressed && this.bladeDashCooldown <= 0) {
      this.bladeDashCooldown = 1.2;
      const dashDir = new THREE.Vector3(-Math.sin(playerYaw), 0, -Math.cos(playerYaw));
      result.recoilImpulse.copy(dashDir.multiplyScalar(-14)); // negative = push forward
      result.hudText = 'INFINITY BLADE  DASH!';
    }
  }

  private spawnBladeArc(origin: THREE.Vector3, yaw: number): void {
    const arcGeo = new THREE.BoxGeometry(0.08, 0.12, 8);
    for (let i = 0; i < 5; i++) {
      const angle = yaw + (i - 2) * (Math.PI / 10); // spread 90° over 5 blades
      const mat = new THREE.MeshBasicMaterial({
        color: 0x88ddff,
        transparent: true,
        opacity: 0.7,
        depthWrite: false,
      });
      const mesh = new THREE.Mesh(arcGeo, mat);
      mesh.position.set(
        origin.x - Math.sin(angle) * 4,
        origin.y + 0.8,
        origin.z - Math.cos(angle) * 4,
      );
      mesh.rotation.y = angle;
      this.scene.add(mesh);
      this.sweepMeshes.push({ mesh, t: 0 });
    }
  }

  // ─── Boom Bow ───────────────────────────────────────────────────────────────

  private updateBoomBow(
    dt: number,
    playerPos: THREE.Vector3,
    playerYaw: number,
    keys: { fire: boolean },
    result: MythicUpdateResult,
  ): void {
    const fireDown = keys.fire;
    const fireReleased = !keys.fire && this.prevFire;

    if (fireDown && !this.boomCharging) {
      this.boomCharging = true;
      this.boomChargeT = 0;
    }

    if (this.boomCharging && fireDown) {
      this.boomChargeT = Math.min(this.boomChargeT + dt, 3);
    }

    if (this.boomCharging && fireReleased) {
      // Fire!
      const stage = Math.max(1, Math.ceil(this.boomChargeT / BOOM_CHARGE_PER_STAGE));
      this.fireBoomArrow(playerPos, playerYaw, stage);
      this.boomCharging = false;
      this.boomChargeT = 0;
      result.recoilImpulse.set(0, 0.02, 0);
    }

    const stageDisplay = this.boomCharging
      ? `[${Math.max(1, Math.ceil(this.boomChargeT / BOOM_CHARGE_PER_STAGE))}] ${'█'.repeat(Math.floor(this.boomChargeT * 4) % 4 + 1)}`
      : '';
    result.hudText = `BOOM BOW  ${stageDisplay}`;
  }

  private fireBoomArrow(origin: THREE.Vector3, yaw: number, stage: number): void {
    const dir = new THREE.Vector3(-Math.sin(yaw), 0.04, -Math.cos(yaw)).normalize();
    const vel = dir.multiplyScalar(BOOM_PROJECTILE_SPEED + stage * 4);

    const geo = new THREE.SphereGeometry(0.12 + stage * 0.05, 8, 8);
    const mat = toonMat(0xff6600, this.gradient, { emissive: 0xff2200, emissiveIntensity: 0.8 });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.copy(origin).add(new THREE.Vector3(0, 0.9, 0));
    this.scene.add(mesh);

    this.boomProjectiles.push({
      mesh,
      vel,
      pos: mesh.position.clone(),
      stage,
      alive: true,
    });
  }

  private updateBoomProjectiles(dt: number, result: MythicUpdateResult): void {
    const GRAVITY = 9.8;
    for (let i = this.boomProjectiles.length - 1; i >= 0; i--) {
      const p = this.boomProjectiles[i]!;
      if (!p.alive) {
        this.boomProjectiles.splice(i, 1);
        continue;
      }

      p.vel.y -= GRAVITY * dt;
      p.pos.addScaledVector(p.vel, dt);
      p.mesh.position.copy(p.pos);

      // Ground hit (y <= 0.1) or lifetime
      if (p.pos.y <= 0.15) {
        this.spawnBoomExplosion(p.pos.clone(), p.stage);
        result.damage = Math.max(result.damage, BOOM_DAMAGE_BASE * p.stage);
        result.damageRadius = BOOM_EXPLOSION_RADIUS;
        p.mesh.removeFromParent();
        (p.mesh.geometry as THREE.BufferGeometry).dispose();
        (p.mesh.material as THREE.Material).dispose();
        this.boomProjectiles.splice(i, 1);
      }
    }
  }

  private spawnBoomExplosion(pos: THREE.Vector3, stage: number): void {
    const geo = new THREE.SphereGeometry(1, 14, 14);
    const mat = new THREE.MeshBasicMaterial({
      color: stage >= 3 ? 0xff2200 : 0xff8800,
      transparent: true,
      opacity: 0.75,
      depthWrite: false,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.copy(pos);
    mesh.scale.setScalar(0);
    this.scene.add(mesh);
    this.boomBlasts.push({ mesh, t: 0 });
  }

  // ─── Grappler ───────────────────────────────────────────────────────────────

  private updateGrappler(
    dt: number,
    playerPos: THREE.Vector3,
    playerYaw: number,
    keys: { fire: boolean },
    result: MythicUpdateResult,
  ): void {
    this.grapplerCooldown = Math.max(0, this.grapplerCooldown - dt);
    const firePressedNow = keys.fire && !this.prevFire;

    if (firePressedNow && this.grapplerCharges > 0 && this.grapplerCooldown <= 0 && !this.activeLine) {
      this.grapplerCharges--;
      this.grapplerCooldown = 0.5;

      // Raycast forward GRAPPLE_RANGE
      const dir = new THREE.Vector3(-Math.sin(playerYaw), 0, -Math.cos(playerYaw));
      const target = playerPos.clone().addScaledVector(dir, GRAPPLE_RANGE);
      target.y = Math.max(0.5, target.y); // keep above ground

      this.fireGrapple(playerPos, target);
    }

    if (this.activeLine) {
      result.grappleTarget = this.grappleLerpResult?.clone() ?? null;
    }

    result.hudText = `GRAPPLER  ${this.grapplerCharges}/${GRAPPLE_CHARGES_MAX}`;
  }

  private fireGrapple(origin: THREE.Vector3, target: THREE.Vector3): void {
    // Build a thin cylinder as the line
    const dir = target.clone().sub(origin);
    const length = dir.length();
    const geo = new THREE.CylinderGeometry(0.04, 0.04, length, 6);
    const mat = new THREE.MeshBasicMaterial({ color: 0xaaaaff });
    const mesh = new THREE.Mesh(geo, mat);

    // Position midpoint
    const mid = origin.clone().add(target).multiplyScalar(0.5);
    mesh.position.copy(mid);

    // Orient toward target
    const axis = new THREE.Vector3(0, 1, 0);
    const normDir = dir.clone().normalize();
    const quaternion = new THREE.Quaternion().setFromUnitVectors(axis, normDir);
    mesh.quaternion.copy(quaternion);

    this.scene.add(mesh);

    this.activeLine = {
      mesh,
      target: target.clone(),
      travelT: 0,
      totalT: GRAPPLE_TRAVEL_TIME,
      startPos: origin.clone(),
    };
    this.grappleLerpResult = origin.clone();
  }

  private updateGrappleLine(start: THREE.Vector3, end: THREE.Vector3): void {
    if (!this.activeLine) return;
    const dir = end.clone().sub(start);
    const length = dir.length();
    if (length < 0.01) return;
    const mid = start.clone().add(end).multiplyScalar(0.5);
    this.activeLine.mesh.position.copy(mid);
    const axis = new THREE.Vector3(0, 1, 0);
    const normDir = dir.clone().normalize();
    const quaternion = new THREE.Quaternion().setFromUnitVectors(axis, normDir);
    this.activeLine.mesh.quaternion.copy(quaternion);

    // Rescale geometry length
    const scale = length / (this.activeLine.totalT * GRAPPLE_TRAVEL_TIME + 0.001);
    // Instead of rescaling geometry, scale Y
    this.activeLine.mesh.scale.y = length;
    this.grappleLerpResult = end.clone();
  }

  // ─── Jetpack ────────────────────────────────────────────────────────────────

  private updateJetpack(
    dt: number,
    keys: { space: boolean },
    result: MythicUpdateResult,
  ): void {
    const grounded = false; // caller can pass this in — we default to regen when space not held
    if (keys.space && this.jetpackFuel > 0) {
      this.jetpackFuel = Math.max(0, this.jetpackFuel - JETPACK_DEPLETE * dt);
      const fuelPct = this.jetpackFuel / JETPACK_FUEL_MAX;
      result.jetpackLift = JETPACK_LIFT_BASE * fuelPct;
    } else {
      // regen
      this.jetpackFuel = Math.min(JETPACK_FUEL_MAX, this.jetpackFuel + JETPACK_REGEN * dt);
      result.jetpackLift = 0;
    }

    const pct = Math.round(this.jetpackFuel * 100);
    const bars = Math.round(this.jetpackFuel * 10);
    result.hudText = `JETPACK  ${'|'.repeat(bars)}${'·'.repeat(10 - bars)}  ${pct}%`;
  }

  // ─── HUD ────────────────────────────────────────────────────────────────────

  private updateHud(): void {
    if (!this.activeKind) {
      this.hudEl.style.display = 'none';
      return;
    }
    const labels: Record<MythicKind, string> = {
      drum_gun: 'DRUM GUN',
      infinity_blade: 'INFINITY BLADE',
      boom_bow: 'BOOM BOW',
      grappler: 'GRAPPLER',
      jetpack: 'JETPACK',
    };
    this.hudEl.textContent = `✦ ${labels[this.activeKind]}`;
    this.hudEl.style.display = 'block';
  }

  // ─── Dispose ────────────────────────────────────────────────────────────────

  dispose(): void {
    for (const item of this.pickups) this.removePickup(item);
    for (const s of this.sweepMeshes) {
      s.mesh.removeFromParent();
      (s.mesh.geometry as THREE.BufferGeometry).dispose();
      (s.mesh.material as THREE.Material).dispose();
    }
    this.sweepMeshes.length = 0;
    for (const p of this.boomProjectiles) {
      p.mesh.removeFromParent();
      (p.mesh.geometry as THREE.BufferGeometry).dispose();
      (p.mesh.material as THREE.Material).dispose();
    }
    this.boomProjectiles.length = 0;
    for (const b of this.boomBlasts) {
      b.mesh.removeFromParent();
      (b.mesh.geometry as THREE.BufferGeometry).dispose();
      (b.mesh.material as THREE.Material).dispose();
    }
    this.boomBlasts.length = 0;
    if (this.activeLine) {
      this.activeLine.mesh.removeFromParent();
      (this.activeLine.mesh.geometry as THREE.BufferGeometry).dispose();
      (this.activeLine.mesh.material as THREE.Material).dispose();
      this.activeLine = null;
    }
    this.gradient.dispose();
    this.hudEl.remove();
  }
}
